import { clamp, el, getSettings, revokeResult, signalPlanChange, state } from "./core.js"

const MIN_CROP = 0.05
let drag = null
let commitTimer = 0

function ensureCrop() {
  if (!state.crop) state.crop = { x: 0, y: 0, width: 1, height: 1 }
  return state.crop
}

function copyCrop(crop = ensureCrop()) {
  return { x: crop.x, y: crop.y, width: crop.width, height: crop.height }
}

function normalizeCrop(crop) {
  crop.width = clamp(Number(crop.width) || 1, MIN_CROP, 1)
  crop.height = clamp(Number(crop.height) || 1, MIN_CROP, 1)
  crop.x = clamp(Number(crop.x) || 0, 0, 1 - crop.width)
  crop.y = clamp(Number(crop.y) || 0, 0, 1 - crop.height)
  return crop
}

export function getCropNormalized() {
  return copyCrop(normalizeCrop(ensureCrop()))
}

export function resetCrop(options = {}) {
  state.crop = { x: 0, y: 0, width: 1, height: 1 }
  updateCropOverlay()
  refreshCropMetrics()
  if (!options.silent) commitCropChange("reset")
}

export function getCropSourceRect() {
  const crop = getCropNormalized()
  return {
    x: Math.round(crop.x * state.sourceWidth),
    y: Math.round(crop.y * state.sourceHeight),
    width: Math.max(1, Math.round(crop.width * state.sourceWidth)),
    height: Math.max(1, Math.round(crop.height * state.sourceHeight)),
  }
}

export function getCroppedOutputDimensions(settings = getSettings()) {
  if (!state.sourceWidth || !state.sourceHeight) return { width: 0, height: 0 }
  const crop = getCropSourceRect()
  const sourceMax = Math.max(crop.width, crop.height)
  const outputMax = Math.max(1, Math.min(sourceMax, sourceMax * settings.scale / 100, settings.resolution))
  const ratio = outputMax / sourceMax
  return {
    width: Math.max(1, Math.round(crop.width * ratio)),
    height: Math.max(1, Math.round(crop.height * ratio)),
  }
}

export function getCropDecodeDimensions(outputDimensions) {
  const crop = getCropNormalized()
  const sourceAspect = state.sourceWidth / Math.max(1, state.sourceHeight)
  const wantedWidth = outputDimensions.width / Math.max(MIN_CROP, crop.width)
  const wantedHeight = outputDimensions.height / Math.max(MIN_CROP, crop.height)
  let width = Math.min(state.sourceWidth, Math.max(outputDimensions.width, Math.ceil(wantedWidth)))
  let height = Math.round(width / sourceAspect)
  if (height < wantedHeight) {
    height = Math.min(state.sourceHeight, Math.max(outputDimensions.height, Math.ceil(wantedHeight)))
    width = Math.round(height * sourceAspect)
  }
  return {
    width: Math.max(1, Math.min(state.sourceWidth, width)),
    height: Math.max(1, Math.min(state.sourceHeight, height)),
  }
}

export function getCropRectForDecoded(dimensions) {
  const crop = getCropNormalized()
  return {
    x: crop.x * dimensions.width,
    y: crop.y * dimensions.height,
    width: Math.max(1, crop.width * dimensions.width),
    height: Math.max(1, crop.height * dimensions.height),
  }
}

function contentRect() {
  const video = el.videoPreview
  const shell = video?.closest(".video-shell")
  if (!video || !shell || !state.sourceWidth || !state.sourceHeight) return null
  const videoRect = video.getBoundingClientRect()
  const shellRect = shell.getBoundingClientRect()
  const boxWidth = videoRect.width
  const boxHeight = videoRect.height
  const sourceAspect = state.sourceWidth / state.sourceHeight
  let width = boxWidth
  let height = width / sourceAspect
  if (height > boxHeight) {
    height = boxHeight
    width = height * sourceAspect
  }
  return {
    left: videoRect.left - shellRect.left + (boxWidth - width) / 2,
    top: videoRect.top - shellRect.top + (boxHeight - height) / 2,
    width,
    height,
  }
}

function installStyles() {
  if (document.getElementById("cropStyles")) return
  const style = document.createElement("style")
  style.id = "cropStyles"
  style.textContent = `
    .crop-frame{position:absolute;z-index:6;border:2px solid #52e6ff;box-shadow:0 0 0 9999px rgba(0,0,0,.48),0 0 0 1px rgba(0,0,0,.8) inset;touch-action:none;cursor:move;min-width:24px;min-height:24px}
    .crop-frame[hidden]{display:none!important}
    .crop-frame::before,.crop-frame::after{content:"";position:absolute;pointer-events:none;background:rgba(82,230,255,.42)}
    .crop-frame::before{left:33.333%;top:0;bottom:0;width:1px;box-shadow:calc(var(--crop-w,90px)/3) 0 0 rgba(82,230,255,.42)}
    .crop-frame::after{top:33.333%;left:0;right:0;height:1px;box-shadow:0 calc(var(--crop-h,90px)/3) 0 rgba(82,230,255,.42)}
    .crop-handle{position:absolute;width:28px;height:28px;border:0;background:transparent;padding:0;touch-action:none}
    .crop-handle::before{content:"";position:absolute;width:13px;height:13px;border-color:#fff;border-style:solid;filter:drop-shadow(0 1px 2px #000)}
    .crop-handle[data-handle="nw"]{left:-15px;top:-15px;cursor:nwse-resize}.crop-handle[data-handle="nw"]::before{right:0;bottom:0;border-width:3px 0 0 3px}
    .crop-handle[data-handle="ne"]{right:-15px;top:-15px;cursor:nesw-resize}.crop-handle[data-handle="ne"]::before{left:0;bottom:0;border-width:3px 3px 0 0}
    .crop-handle[data-handle="sw"]{left:-15px;bottom:-15px;cursor:nesw-resize}.crop-handle[data-handle="sw"]::before{right:0;top:0;border-width:0 0 3px 3px}
    .crop-handle[data-handle="se"]{right:-15px;bottom:-15px;cursor:nwse-resize}.crop-handle[data-handle="se"]::before{left:0;top:0;border-width:0 3px 3px 0}
    .crop-toolbar{position:absolute;z-index:11;left:54px;bottom:8px;display:flex;gap:6px;align-items:center;padding:4px 6px;border:1px solid rgba(255,255,255,.18);border-radius:999px;background:rgba(0,0,0,.7);font-size:.67rem}
    .crop-toolbar button{border:0;border-radius:999px;padding:4px 8px;background:rgba(255,255,255,.12);color:#fff;font-weight:800}
    .crop-toolbar span{color:#a3f3ff;font-weight:800;white-space:nowrap}
  `
  document.head.appendChild(style)
}

function ensureUi() {
  installStyles()
  const shell = el.videoPreview?.closest(".video-shell")
  if (!shell) return null
  let frame = document.getElementById("cropFrame")
  if (!frame) {
    frame = document.createElement("div")
    frame.id = "cropFrame"
    frame.className = "crop-frame"
    frame.hidden = true
    frame.setAttribute("aria-label", "Video crop frame")
    frame.innerHTML = ["nw", "ne", "sw", "se"].map((handle) => `<button type="button" class="crop-handle" data-handle="${handle}" aria-label="Resize crop ${handle}"></button>`).join("")
    shell.appendChild(frame)
    const toolbar = document.createElement("div")
    toolbar.className = "crop-toolbar"
    toolbar.id = "cropToolbar"
    toolbar.innerHTML = '<button id="cropReset" type="button">Reset crop</button><span id="cropSize">Full frame</span>'
    shell.appendChild(toolbar)
    toolbar.querySelector("button").addEventListener("click", () => resetCrop())
    frame.addEventListener("pointerdown", beginDrag)
    frame.addEventListener("pointermove", moveDrag)
    frame.addEventListener("pointerup", endDrag)
    frame.addEventListener("pointercancel", endDrag)
  }
  return frame
}

export function updateCropOverlay() {
  const frame = ensureUi()
  const rect = contentRect()
  if (!frame || !rect) {
    if (frame) frame.hidden = true
    return
  }
  const crop = getCropNormalized()
  const width = crop.width * rect.width
  const height = crop.height * rect.height
  frame.hidden = false
  frame.style.left = `${rect.left + crop.x * rect.width}px`
  frame.style.top = `${rect.top + crop.y * rect.height}px`
  frame.style.width = `${width}px`
  frame.style.height = `${height}px`
  frame.style.setProperty("--crop-w", `${width}px`)
  frame.style.setProperty("--crop-h", `${height}px`)
  refreshCropMetrics()
}

function beginDrag(event) {
  if (event.button != null && event.button !== 0) return
  event.preventDefault()
  const frame = event.currentTarget
  const rect = contentRect()
  if (!rect) return
  drag = {
    pointerId: event.pointerId,
    mode: event.target.closest("[data-handle]")?.dataset.handle || "move",
    startX: event.clientX,
    startY: event.clientY,
    crop: getCropNormalized(),
    rect,
  }
  frame.setPointerCapture?.(event.pointerId)
}

function moveDrag(event) {
  if (!drag || event.pointerId !== drag.pointerId) return
  event.preventDefault()
  const dx = (event.clientX - drag.startX) / Math.max(1, drag.rect.width)
  const dy = (event.clientY - drag.startY) / Math.max(1, drag.rect.height)
  const start = drag.crop
  let next = copyCrop(start)

  if (drag.mode === "move") {
    next.x = clamp(start.x + dx, 0, 1 - start.width)
    next.y = clamp(start.y + dy, 0, 1 - start.height)
  } else {
    const west = drag.mode.includes("w")
    const east = drag.mode.includes("e")
    const north = drag.mode.includes("n")
    const south = drag.mode.includes("s")
    if (west) {
      const right = start.x + start.width
      next.x = clamp(start.x + dx, 0, right - MIN_CROP)
      next.width = right - next.x
    }
    if (east) next.width = clamp(start.width + dx, MIN_CROP, 1 - start.x)
    if (north) {
      const bottom = start.y + start.height
      next.y = clamp(start.y + dy, 0, bottom - MIN_CROP)
      next.height = bottom - next.y
    }
    if (south) next.height = clamp(start.height + dy, MIN_CROP, 1 - start.y)
  }

  state.crop = normalizeCrop(next)
  updateCropOverlay()
  revokeResult(true)
}

function endDrag(event) {
  if (!drag || event.pointerId !== drag.pointerId) return
  drag = null
  commitCropChange("drag")
}

function commitCropChange(reason) {
  clearTimeout(commitTimer)
  commitTimer = setTimeout(() => {
    dispatchEvent(new CustomEvent("converter-crop-change", { detail: { crop: getCropNormalized(), reason } }))
    signalPlanChange("crop")
  }, 0)
}

export function refreshCropMetrics() {
  const label = document.getElementById("cropSize")
  const dimensions = getCroppedOutputDimensions()
  const crop = getCropSourceRect()
  if (label) label.textContent = state.sourceWidth ? `${crop.width}×${crop.height}` : "Full frame"
  if (state.file && state.mediaReady && el.statResolution && dimensions.width) {
    el.statResolution.textContent = `${dimensions.width}×${dimensions.height}`
  }
}

export function setupCropFrame() {
  ensureCrop()
  ensureUi()
  addEventListener("resize", updateCropOverlay)
  el.videoPreview?.addEventListener("loadedmetadata", () => requestAnimationFrame(updateCropOverlay))
  el.videoPreview?.addEventListener("loadeddata", () => requestAnimationFrame(updateCropOverlay))
  addEventListener("converter-plan-change", () => requestAnimationFrame(refreshCropMetrics))
  updateCropOverlay()
}
